import { colors } from '@ardoq/design-tokens';
import { DEFAULT_WINDOW_CENTER } from '../consts';
import {
  ChartDimensions,
  ChartWindowState,
  NumericRange,
  TimeRange,
  rect,
  timeScaler,
  timeUnscaler,
} from '@ardoq/graph';
import { startOfToday } from '@ardoq/date-time';
import { clamp } from 'lodash';

export const getGroupSeparatorStyle = (
  isLabelsColumn: boolean,
  showGroupSeparator?: boolean
): React.CSSProperties | undefined => {
  const borderColor = isLabelsColumn ? 'transparent' : colors.grey400;
  return showGroupSeparator
    ? {
        borderBottom: `1px dashed ${borderColor}`,
      }
    : undefined;
};

/**
 * given that the total available space is 1, and the current window center position is x, the window
 * center is the ratio of the current window center position to the total unscaled chart width
 */
export const getWindowCenterXForChartWindowLeftPosition = (
  unscaledChartWidth: number,
  windowScale: number,
  chartWindowScrollLeft: number
) => {
  const visibleChartWindowWidth = unscaledChartWidth * windowScale;

  const currentWindowCenterPosition =
    chartWindowScrollLeft + visibleChartWindowWidth * 0.5;

  return currentWindowCenterPosition / unscaledChartWidth;
};

/**
 * current window center position minus distance from windowLeft to windowCenter === chartWindowScrollLeft
 */
export const getChartWindowScrollLeftForWindowCenterX = (
  unscaledChartWidth: number,
  windowCenterX: number,
  windowScale: number
) => unscaledChartWidth * (windowCenterX - windowScale * 0.5);

export const sanitizeWindowCenterXValue = (
  windowScale: number,
  windowCenterX: number
) => {
  if (windowScale >= 1) {
    return DEFAULT_WINDOW_CENTER[0];
  }

  const minWindowCenterX = DEFAULT_WINDOW_CENTER[0] * windowScale;
  const maxWindowCenterX = 1 - minWindowCenterX;

  return clamp(windowCenterX, minWindowCenterX, maxWindowCenterX);
};

type GetChartDomainParametersArgs = ChartWindowState & {
  availableExpandedDomain: TimeRange;
  dimensions: ChartDimensions;
};
export const getVisibleChartDomainWithScalers = ({
  windowCenter,
  windowScale,
  availableExpandedDomain,
  dimensions,
}: GetChartDomainParametersArgs) => {
  const { left, right } = rect(windowCenter, windowScale);

  const pixelsX: NumericRange = [
    dimensions.margin.left,
    dimensions.margin.left + dimensions.chart.width,
  ];

  const unscaleX = timeUnscaler(pixelsX, availableExpandedDomain, [
    left,
    right,
  ]);
  const visibleDomain: TimeRange = [unscaleX(pixelsX[0]), unscaleX(pixelsX[1])];

  const scaleX = timeScaler(pixelsX, availableExpandedDomain, [left, right]);

  return {
    scaleX,
    unscaleX,
    visibleDomain,
    todayPosition: scaleX(startOfToday()),
  };
};

type GetWindowCenterXFromScrollLeftArgs = {
  chartWidth: number;
  windowScale: number;
  scrollLeft: number;
};
export const getWindowCenterXFromScrollLeft = ({
  chartWidth,
  windowScale,
  scrollLeft,
}: GetWindowCenterXFromScrollLeftArgs) => {
  // Event.target.scrollLeft is floored. Account for the last pixel of the chart range,
  // that would be unreached due to floored scrollLeft value
  const isLastPixel = chartWidth - (scrollLeft + chartWidth * windowScale) <= 1;
  const adjustedScrollLeft = isLastPixel ? scrollLeft + 1 : scrollLeft;

  const nextWindowCenterX = getWindowCenterXForChartWindowLeftPosition(
    chartWidth,
    windowScale,
    adjustedScrollLeft
  );

  return sanitizeWindowCenterXValue(windowScale, nextWindowCenterX);
};
